import { resolveApiPath } from '@/services/api';
import type { EnvironmentMode } from '@/contexts/EnvironmentContext';
import { isPageExiting } from '@/lib/pageLifecycle';

// SSE Client for real-time streaming updates backed by the platform API.
export class SSEClient {
  private eventSource: EventSource | null = null;
  private reconnectTimeout: number | null = null;
  private reconnectDelay = 1000; // Start with 1 second
  private readonly maxReconnectDelay = 30000; // Max 30 seconds
  private environment: EnvironmentMode = 'demo';
  private readonly handlers: Map<string, Set<(data: unknown) => void>> = new Map();
  private readonly closedSources = new WeakSet<EventSource>();

  setEnvironment(env: EnvironmentMode) {
    this.environment = env;
  }

  connect() {
    const demoMode = import.meta.env.VITE_DEMO_MODE === '1';
    const demoSseEnabled = import.meta.env.VITE_ENABLE_SSE === '1';
    // In demo mode we only connect SSE when explicitly enabled.
    if (demoMode && !demoSseEnabled) {
      return;
    }

    if (this.eventSource && this.eventSource.readyState === EventSource.OPEN) {
      return;
    }

    const streamUrl = resolveApiPath('/api/stream');

    try {
      const source = new EventSource(streamUrl, { withCredentials: true });
      this.eventSource = source;

      source.onopen = () => {
        console.log('SSE connection established');
        this.reconnectDelay = 1000;
      };

      source.onerror = (error) => {
        if (this.closedSources.has(source) || isPageExiting()) {
          return;
        }
        console.error('SSE connection error:', error);
        this.scheduleReconnect();
      };

      source.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.emit('message', data);
        } catch (err) {
          console.error('Failed to parse SSE message:', err);
        }
      };

      const subscribe = (type: string) => {
        source.addEventListener(type, (event) => {
          try {
            const data = JSON.parse(event.data);
            this.emit(type, data);
          } catch (err) {
            console.error(`Failed to parse SSE event ${type}:`, err);
          }
        });
      };

      [
        'botStatus',
        'bot_state_change',
        'bot_heartbeat',
        'metricsTick',
        'complianceAlert',
        'backtestProgress',
        'orderUpdate',
        'positionUpdate',
        'marketUpdate',
        'heartbeat',
        'tenantActivity',
        'tenantApprovals',
        'tenantSummary',
      ].forEach(subscribe);
    } catch (error) {
      console.error('Failed to create SSE connection:', error);
      this.scheduleReconnect();
    }
  }

  private closeSource(source: EventSource) {
    this.closedSources.add(source);
    source.close();
  }

  private isLoggedOut(): boolean {
    try {
      const timestamp = localStorage.getItem('rugira_logout_guard');
      if (timestamp) {
        const guardAge = Date.now() - Number.parseInt(timestamp, 10);
        if (guardAge < 30000) {
          return true;
        }
      }
      if (sessionStorage.getItem('rugira_logout_guard') === '1') {
        return true;
      }
    } catch {
      // ignore
    }
    return false;
  }

  private scheduleReconnect() {
    const demoMode = import.meta.env.VITE_DEMO_MODE === '1';
    const demoSseEnabled = import.meta.env.VITE_ENABLE_SSE === '1';
    if (demoMode && !demoSseEnabled) {
      return;
    }

    // Don't reconnect if user has logged out
    if (this.isLoggedOut()) {
      console.log('SSE reconnect skipped - user logged out');
      if (this.eventSource) {
        this.closeSource(this.eventSource);
        this.eventSource = null;
      }
      return;
    }

    if (this.eventSource) {
      this.closeSource(this.eventSource);
      this.eventSource = null;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectTimeout = window.setTimeout(() => {
      // Double-check logout status before reconnecting
      if (this.isLoggedOut()) {
        console.log('SSE reconnect cancelled - user logged out');
        return;
      }
      console.log(`Attempting to reconnect SSE (delay: ${this.reconnectDelay}ms)`);
      this.connect();
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay);
    }, this.reconnectDelay);
  }

  on(eventType: string, handler: (data: unknown) => void) {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set());
    }
    this.handlers.get(eventType)!.add(handler);
  }

  off(eventType: string, handler: (data: unknown) => void) {
    const handlers = this.handlers.get(eventType);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  private emit(eventType: string, data: unknown) {
    const handlers = this.handlers.get(eventType);
    if (!handlers) return;

    handlers.forEach((handler) => {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error in ${eventType} handler:`, error);
      }
    });
  }

  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.eventSource) {
      this.closeSource(this.eventSource);
      this.eventSource = null;
    }

    this.reconnectDelay = 1000;
  }

  emitEvent(eventType: string, data: unknown) {
    this.emit(eventType, data);
  }
}

export const sseClient = new SSEClient();

export default sseClient;
